import { useEffect, useState } from 'react';
import type { BrandContentResponseModel, PreferenceDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { Icon } from '@/components/ui/icon';
import { FiltersAlphabetical } from '@/components/shared/filters/alphabetical';

export type ShopBrandsAllProps = {
  brands: BrandContentResponseModel[];
  userPreferences: PreferenceDto[];
  hasActiveMembership?: boolean;
  onBookmarkToggle: (brand: BrandContentResponseModel, isSelected: boolean) => void;
  onBrandClick: (brand: BrandContentResponseModel) => void;
};

export function ShopBrandsAll({
  brands,
  userPreferences,
  hasActiveMembership,
  onBookmarkToggle,
  onBrandClick,
}: ShopBrandsAllProps) {
  const t = useClientTranslation();
  const [scrollOffset, setScrollOffset] = useState(82);

  useEffect(() => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:env(safe-area-inset-top,0px);width:0;height:0;visibility:hidden;pointer-events:none;';
    document.body.appendChild(probe);
    const safeTop = parseFloat(getComputedStyle(probe).top) || 0;
    document.body.removeChild(probe);
    setScrollOffset(82 + safeTop);
  }, []);

  return (
    <div className="relative px-4 pr-10.5 pb-25">
      <FiltersAlphabetical items={brands} getLabel={b => b.name ?? ''} scrollOffset={scrollOffset}>
        {groupBrands => (
          <div className="flex flex-col">
            {groupBrands.map(brand => {
              const isSelected = userPreferences.some(p => p.subcategoryNodeKey === brand.id);
              const isDisabled = (brand.properties?.membersOnly ?? false) && !hasActiveMembership;

              if (isDisabled) {
                return (
                  <div
                    key={brand.id}
                    className="border-jncquoi-neutral-200 flex items-center gap-4 border-b py-4"
                  >
                    <Icon
                      name="bookmark"
                      width={16}
                      height={16}
                      className="text-jncquoi-neutral-700 shrink-0"
                    />
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[12px] tracking-[0.5px] uppercase">
                      {brand.name}
                    </span>
                    <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] whitespace-nowrap uppercase">
                      {t('App.Labels.MembersOnly')}
                    </span>
                  </div>
                );
              }

              return (
                <div
                  key={brand.id}
                  className="border-jncquoi-neutral-200 flex items-center gap-4 border-b py-4"
                >
                  <button
                    type="button"
                    onClick={() => onBookmarkToggle(brand, isSelected)}
                    className="flex shrink-0 items-center justify-center"
                  >
                    <Icon
                      name={isSelected ? 'bookmark-solid' : 'bookmark'}
                      width={16}
                      height={16}
                      className="text-jncquoi-neutral-1000"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => onBrandClick(brand)}
                    className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-left text-[12px] tracking-[0.5px] uppercase"
                  >
                    {brand.name}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </FiltersAlphabetical>
    </div>
  );
}
